import React from "react";
import { DatePicker } from "@/components/ui/date-picker";
import { dateToLocalString, getToday, parseSelectableDate } from "../utils/dateUtils";

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  className?: string;
  hasError?: boolean;
  disabled?: boolean;
  // optional limits (YYYY-MM-DD or any parsable date string)
  minDate?: string;
  maxDate?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startDatePlaceholder = "From",
  endDatePlaceholder = "To",
  hasError = false,
  disabled = false,
  minDate,
  maxDate
}) => {
  const parsedMin = parseSelectableDate(minDate);
  const parsedMax = parseSelectableDate(maxDate);

  const getMinStartDate = () => {
    // Usa il min passato esplicitamente, altrimenti nessun limite
    return parsedMin;
  };

  const getMaxStartDate = () => {
    const end = endDate ? parseSelectableDate(endDate) : undefined;
    if (end && parsedMax) {
      return new Date(Math.min(end.getTime(), parsedMax.getTime()));
    }
    return end ?? parsedMax;
  };

  const getMinEndDate = () => {
    const start = startDate ? parseSelectableDate(startDate) : undefined;
    if (start && parsedMin) {
      return start > parsedMin ? start : parsedMin;
    }
    if (start) return start;
    return parsedMin; // Rimosso getToday()
  };

  const getMaxEndDate = () => {
    return parsedMax; // undefined if not provided
  };

  const handleStartDateChange = (date: string) => {
    const startDateObj = new Date(date);

    // Se la end date esiste ed è prima della start date, pulisci la end date
    if (endDate && startDateObj > new Date(endDate)) {
      onEndDateChange('');
    }

    onStartDateChange(date);
  };

  const handleEndDateChange = (date: string) => {
    onEndDateChange(date);
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Start Date */}
      <div className="space-y-2">
        <DatePicker
          value={startDate}
          onSelect={handleStartDateChange}
          placeholder={startDatePlaceholder}
          minDate={getMinStartDate()}
          maxDate={getMaxStartDate()}
          hasError={hasError}
          isDisabled={disabled}
        />
      </div>

      {/* End Date */}
      <div className="space-y-2">
        <DatePicker
          value={endDate}
          onSelect={handleEndDateChange}
          placeholder={endDatePlaceholder}
          minDate={getMinEndDate()}
          maxDate={getMaxEndDate()}
          hasError={hasError}
          isDisabled={disabled}
        />
      </div>
    </div>
  );
};
